import { AppIcon } from '@/components/ui/app-icon';
import {
  CreditsDropdown,
  type CreditsSummary,
} from '@/components/credits-dropdown.component';
import { NotificationsDropdown } from '@/components/notifications-dropdown';
import { DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { UserMenu } from '@/components/user-menu.component';
import { WhatsNewButton } from '@/components/whats-new/whats-new-button.component';
import { DialerButton } from '@/containers/calling/dialer-button.component';
import { requestChatHistoryRail } from '@/containers/copilot/chat-history-rail.util';
import {
  showIntercom,
  useIntercomUnreadCount,
} from '@/hooks/use-intercom.hook';
import {
  iconBoxArchive,
  iconChevronDown,
  iconCircleQuestion,
  iconCoins,
  iconGear,
  iconSpinnerThird,
} from '@/lib/icons';
import { formatCredits } from '@/lib/credits';
import { cn } from '@/lib/utils';
import { OrganizationSwitcher } from '@clerk/react';
import { Link, useNavigate } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import type { CSSProperties } from 'react';
import { SidebarPresence } from '../sidebar-presence.component';
import { SidebarUpgradeCta } from '../sidebar-upgrade-cta.component';
import { SidebarIconButton } from './sidebar-icon-button.component';
import { SidebarRule, SidebarTooltip } from './sidebar-tooltip.component';
import {
  RAIL_CELL,
  RAIL_GRID,
  RAIL_RULE,
  RAIL_SLOT,
  SIDEBAR_HOVER,
  SIDEBAR_RULE,
} from './tabs-sidebar.styles';

/** Where the credits menu sends people to buy credits, and so does Buy more. */
const PLANS_SEARCH = {
  credits_added: undefined,
  plan_name: undefined,
  status: undefined,
  tab: undefined,
};

/** The gauge's colour: the credits menu's warning and critical, else blue. */
function gaugeTone(status: CreditsSummary['status'], kind: 'bg' | 'stroke') {
  if (status === 'critical')
    return kind === 'bg' ? 'bg-rose-500' : 'stroke-rose-500';
  if (status === 'warning')
    return kind === 'bg' ? 'bg-amber-500' : 'stroke-amber-500';
  return kind === 'bg' ? 'bg-primary' : 'stroke-primary';
}

/** The coin in the rail's credits gauge: muted, or the warning's colour. */
function coinTone(status: CreditsSummary['status']) {
  if (status === 'critical') return 'text-rose-500';
  if (status === 'warning') return 'text-amber-500';
  return 'text-gray-500 dark:text-gray-400';
}

/** Share of the allowance left, as a whole percent from 0 to 100. */
const percentLeft = (percentage: number | null) =>
  Math.round(Math.max(0, Math.min(1, percentage ?? 0)) * 100);

/**
 * What the top bar's right side held, under the Upgrade button when the
 * workspace has one: the user with the organization under the name, as one
 * control whose menu also switches organization, beside the bell, the dialer
 * and the Settings gear; then credits, and the bottom bar with chat history and help
 * and support. Folded, most are a cell of the page rail (see RailFooter). The
 * gear is how the open sidebar reaches Settings, so it is selected on a
 * Settings page, as a row is for its page.
 */
export function SidebarFooter({
  chatsAvailable,
  collapsed = false,
  settingsActive = false,
}: {
  chatsAvailable: boolean;
  collapsed?: boolean;
  /** A Settings page is on screen, with no chat in front of it. */
  settingsActive?: boolean;
}) {
  if (collapsed) {
    return <RailFooter />;
  }
  return (
    <>
      <SidebarUpgradeCta className='pt-2 pb-2.5' />
      <div className='shrink-0 border-t border-black/10 pt-1 text-sm dark:border-white/10'>
        <HiddenOrganizationSwitcher />
        <div className='flex h-10 items-center gap-0.5 pl-0.5'>
          <UserMenu />
          <NotificationsDropdown />
          <DialerButton />
          <SettingsGear active={settingsActive} />
        </div>
        <CreditsDropdown
          align='end'
          side='right'
          renderTrigger={(summary) => <CreditsRow {...summary} />}
        />
      </div>
      <BottomBar chatsAvailable={chatsAvailable} />
    </>
  );
}

/**
 * The footer on the page rail: the account, the bell and credits, then
 * What's new and help, each a cell on the rail's centre line, between short
 * rules. Settings is in the account menu, so the rail has no gear, and no
 * count of who else is here. Chat history is in Clu's flyout.
 */
function RailFooter() {
  return (
    <div className='flex shrink-0 flex-col items-center'>
      <SidebarUpgradeCta collapsed className='mt-2 mb-1' />
      <SidebarRule className={RAIL_RULE} />
      <div className={RAIL_GRID}>
        {/* The account menu and the bell draw their own triggers; their
            slots size them to the cells around them. */}
        <div className={RAIL_SLOT}>
          <UserMenu />
        </div>
        <div className={RAIL_SLOT}>
          <NotificationsDropdown />
        </div>
        <div className={RAIL_SLOT}>
          <DialerButton rail />
        </div>
        <CreditsDropdown
          align='end'
          side='right'
          renderTrigger={(summary) => <RailCredits {...summary} />}
        />
      </div>
      <SidebarRule className={RAIL_RULE} />
      <div className={RAIL_GRID}>
        <div className={RAIL_SLOT}>
          <WhatsNewButton collapsed iconSize={16} />
        </div>
        <HelpButton rail />
      </div>
      <HiddenOrganizationSwitcher />
    </div>
  );
}

/** The Settings gear, 28px beside the bell. */
function SettingsGear({ active }: { active: boolean }) {
  return (
    <SidebarIconButton
      label='Settings'
      icon={iconGear}
      to='/settings'
      active={active}
      side='top'
    />
  );
}

/**
 * Clerk's organization switcher mounted hidden, to keep the active
 * organization's context. Switching is in the account menu.
 */
function HiddenOrganizationSwitcher() {
  return (
    <div className='hidden'>
      <OrganizationSwitcher hidePersonal />
    </div>
  );
}

/**
 * Credits on the page rail: a ring of what is left around a coin, so it
 * reads as credits even when empty; its tooltip says how many are left.
 */
function RailCredits({
  credits,
  isLoading,
  percentage,
  status,
}: CreditsSummary) {
  const label = isLoading ? 'Credits' : `${formatCredits(credits)} credits`;
  const radius = 8.5;
  const circumference = 2 * Math.PI * radius;
  const arc = (percentLeft(percentage) / 100) * circumference;
  return (
    <SidebarTooltip label={label}>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          aria-label={label}
          className={cn(
            'grid place-items-center transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50',
            RAIL_CELL,
            SIDEBAR_HOVER
          )}
        >
          <svg
            width='21'
            height='21'
            viewBox='0 0 21 21'
            fill='none'
            strokeWidth='2'
            aria-hidden
            className='col-start-1 row-start-1'
          >
            <circle
              cx='10.5'
              cy='10.5'
              r={radius}
              className='stroke-black/[0.12] dark:stroke-white/[0.14]'
            />
            {arc > 0 && (
              <circle
                cx='10.5'
                cy='10.5'
                r={radius}
                strokeLinecap='round'
                className={gaugeTone(status, 'stroke')}
                strokeDasharray={`${arc} ${circumference}`}
                transform='rotate(-90 10.5 10.5)'
              />
            )}
          </svg>
          <AppIcon
            icon={isLoading ? iconSpinnerThird : iconCoins}
            aria-hidden
            className={cn(
              'col-start-1 row-start-1',
              isLoading
                ? 'animate-spin text-muted-foreground'
                : coinTone(status)
            )}
            style={{ fontSize: 9 }}
          />
        </button>
      </DropdownMenuTrigger>
    </SidebarTooltip>
  );
}

/** The balance, a thin gauge of what is left, and Buy more. */
function CreditsRow({
  credits,
  isLoading,
  percentage,
  status,
}: CreditsSummary) {
  const posthog = usePostHog();
  const left = percentLeft(percentage);
  return (
    <div className='flex h-7 items-center gap-2 pr-0.5 pl-2 text-xs'>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          aria-label={isLoading ? 'Credits' : undefined}
          className='flex shrink-0 items-center gap-1 rounded font-medium whitespace-nowrap text-foreground hover:opacity-80 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50'
        >
          {isLoading ? (
            <AppIcon
              icon={iconSpinnerThird}
              className='animate-spin text-muted-foreground'
              style={{ fontSize: 10 }}
            />
          ) : (
            `${formatCredits(credits)} credits`
          )}
          <AppIcon
            icon={iconChevronDown}
            className='text-muted-foreground'
            style={{ fontSize: 9 }}
          />
        </button>
      </DropdownMenuTrigger>
      <span
        role='meter'
        aria-label='Credit balance'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={left}
        // An unknown balance is not 0 left.
        aria-valuetext={percentage === null ? 'Balance unknown' : undefined}
        className={cn(
          'h-1 min-w-0 flex-1 overflow-hidden rounded-sm',
          SIDEBAR_RULE
        )}
      >
        <span
          className={cn('block h-1', gaugeTone(status, 'bg'))}
          style={{ width: `${left}%` }}
        />
      </span>
      <Link
        to='/plans'
        search={PLANS_SEARCH}
        aria-label='Buy more credits'
        onClick={() =>
          posthog?.capture('sidebar_buy_more_clicked', {
            credits_status: status,
            percent_left: left,
          })
        }
        className={cn(
          'grid h-6 shrink-0 place-items-center rounded-md px-1.5 font-medium whitespace-nowrap text-[#1F4FD1] dark:text-primary',
          SIDEBAR_HOVER
        )}
      >
        Buy more
      </Link>
    </div>
  );
}

/** Chat history on the left, help and support on the right. */
function BottomBar({ chatsAvailable }: { chatsAvailable: boolean }) {
  const navigate = useNavigate();
  return (
    <div className='flex h-8 shrink-0 items-center justify-between'>
      {chatsAvailable ? (
        <SidebarIconButton
          label='Chat history'
          icon={iconBoxArchive}
          side='top'
          onClick={() => {
            requestChatHistoryRail();
            void navigate({ to: '/clu' });
          }}
        />
      ) : (
        <span />
      )}
      {/* Who else has the app open; it hides itself for a solo organization
          and is not on the rail. Its avatar rings take the
          sidebar's tint. */}
      <div
        className='min-w-0 flex-1'
        style={{ '--presence-ring': 'var(--sidebar-tint)' } as CSSProperties}
      >
        <SidebarPresence />
      </div>
      <WhatsNewButton />
      <HelpButton />
    </div>
  );
}

/**
 * Help and support, with a red dot while Intercom has unread messages: 28px
 * open, or a cell of the page rail.
 */
function HelpButton({ rail = false }: { rail?: boolean }) {
  const intercomUnread = useIntercomUnreadCount();
  return (
    <SidebarIconButton
      label={
        intercomUnread > 0
          ? 'Help and support, unread messages'
          : 'Help and support'
      }
      tooltip='Help and support'
      icon={iconCircleQuestion}
      side={rail ? 'right' : 'top'}
      className={rail ? RAIL_CELL : undefined}
      iconSize={rail ? 16 : undefined}
      onClick={showIntercom}
    >
      {intercomUnread > 0 && (
        <span
          aria-hidden
          className={cn(
            'absolute h-[7px] w-[7px] rounded-full bg-[#EF4444] ring-[1.5px] ring-[hsl(var(--sidebar-tint))]',
            rail ? 'top-1.5 right-2.5' : 'top-1 right-1'
          )}
        />
      )}
    </SidebarIconButton>
  );
}
